import { toast } from 'react-toastify'

export const normalizeUnitSyncPreview = data => {
  if (!data) {
    return data
  }

  const read = (snakeKey, camelKey, fallback = null) => {
    if (data[snakeKey] !== undefined) {
      return data[snakeKey]
    }

    if (data[camelKey] !== undefined) {
      return data[camelKey]
    }

    return fallback
  }

  return {
    ...data,
    reference_address: read('reference_address', 'referenceAddress'),
    reference_uprn: read('reference_uprn', 'referenceUprn'),
    search_mode: read('search_mode', 'searchMode', 'postcode_only'),
    checked_postcodes: read('checked_postcodes', 'checkedPostcodes', []),
    nearby_postcodes: read('nearby_postcodes', 'nearbyPostcodes', []),
    nearby_expanded: read('nearby_expanded', 'nearbyExpanded', false),
    nearby_attempted_radii: read(
      'nearby_attempted_radii',
      'nearbyAttemptedRadii',
      []
    ),
    nearby_found_radius: read('nearby_found_radius', 'nearbyFoundRadius'),
    nearby_search_result: read(
      'nearby_search_result',
      'nearbySearchResult',
      'not_attempted'
    ),
    nearby_search_max_radius_reached: read(
      'nearby_search_max_radius_reached',
      'nearbySearchMaxRadiusReached',
      false
    ),
    nearby_current_radius: read('nearby_current_radius', 'nearbyCurrentRadius'),
    nearby_next_radius: read('nearby_next_radius', 'nearbyNextRadius'),
    nearby_radius_index: read('nearby_radius_index', 'nearbyRadiusIndex'),
    available_nearby_radii: read(
      'available_nearby_radii',
      'availableNearbyRadii',
      []
    ),
    nearby_radius_stats: read('nearby_radius_stats', 'nearbyRadiusStats', []),
    reference_postcode_pages_checked: read(
      'reference_postcode_pages_checked',
      'referencePostcodePagesChecked',
      0
    ),
    reference_postcode_pages_available: read(
      'reference_postcode_pages_available',
      'referencePostcodePagesAvailable'
    ),
    reference_postcode_page_limit_reached: read(
      'reference_postcode_page_limit_reached',
      'referencePostcodePageLimitReached',
      false
    ),
    reference_postcode_raw_count: read(
      'reference_postcode_raw_count',
      'referencePostcodeRawCount',
      0
    ),
    reference_postcode_deduped_count: read(
      'reference_postcode_deduped_count',
      'referencePostcodeDedupedCount',
      0
    ),
    reference_postcode_matched_count: read(
      'reference_postcode_matched_count',
      'referencePostcodeMatchedCount',
      0
    ),
    reference_postcode: read('reference_postcode', 'referencePostcode'),
    nearby_discovered_postcodes_count: read(
      'nearby_discovered_postcodes_count',
      'nearbyDiscoveredPostcodesCount',
      0
    ),
    nearby_discovered_postcodes_total_count: read(
      'nearby_discovered_postcodes_total_count',
      'nearbyDiscoveredPostcodesTotalCount',
      0
    ),
    nearby_postcode_limit_reached: read(
      'nearby_postcode_limit_reached',
      'nearbyPostcodeLimitReached',
      false
    ),
    nearby_postcodes_pages_checked: read(
      'nearby_postcodes_pages_checked',
      'nearbyPostcodesPagesChecked',
      0
    ),
    nearby_postcodes_pages_available: read(
      'nearby_postcodes_pages_available',
      'nearbyPostcodesPagesAvailable',
      []
    ),
    nearby_postcodes_page_limit_reached: read(
      'nearby_postcodes_page_limit_reached',
      'nearbyPostcodesPageLimitReached',
      false
    ),
    nearby_reversegeo_page_limit_reached: read(
      'nearby_reversegeo_page_limit_reached',
      'nearbyReversegeoPageLimitReached',
      false
    ),
    nearby_raw_count: read('nearby_raw_count', 'nearbyRawCount', 0),
    nearby_deduped_count: read('nearby_deduped_count', 'nearbyDedupedCount', 0),
    matched_from_reference_postcode_count: read(
      'matched_from_reference_postcode_count',
      'matchedFromReferencePostcodeCount',
      0
    ),
    matched_from_nearby_postcodes_count: read(
      'matched_from_nearby_postcodes_count',
      'matchedFromNearbyPostcodesCount',
      0
    ),
    unmatched_from_reference_postcode_count: read(
      'unmatched_from_reference_postcode_count',
      'unmatchedFromReferencePostcodeCount',
      0
    ),
    unmatched_from_nearby_postcodes_count: read(
      'unmatched_from_nearby_postcodes_count',
      'unmatchedFromNearbyPostcodesCount',
      0
    ),
    preview_result_summary: read(
      'preview_result_summary',
      'previewResultSummary'
    )
  }
}

export const normalizeUnitReconciliationPreview = data => {
  if (!data) {
    return data
  }

  const read = (snakeKey, camelKey, fallback = null) => {
    if (data[snakeKey] !== undefined) {
      return data[snakeKey]
    }

    if (data[camelKey] !== undefined) {
      return data[camelKey]
    }

    return fallback
  }

  const preview = read('preview', 'preview')
  const summary = read('summary', 'summary', {})
  const apiUnits = read('api_units', 'apiUnits', [])
  const existingUnits = read('existing_units', 'existingUnits', [])
  const suggestedMatches = read('suggested_matches', 'suggestedMatches', [])

  const readNested = (value, snakeKey, camelKey, fallback = null) => {
    if (value?.[snakeKey] !== undefined) {
      return value[snakeKey]
    }

    if (value?.[camelKey] !== undefined) {
      return value[camelKey]
    }

    return fallback
  }

  const normalizeSummary = value => ({
    ...value,
    existing_units_count: readNested(
      value,
      'existing_units_count',
      'existingUnitsCount',
      0
    ),
    existing_units_with_uprn_count: readNested(
      value,
      'existing_units_with_uprn_count',
      'existingUnitsWithUprnCount',
      0
    ),
    existing_units_with_linked_records_count: readNested(
      value,
      'existing_units_with_linked_records_count',
      'existingUnitsWithLinkedRecordsCount',
      0
    ),
    api_units_count: readNested(value, 'api_units_count', 'apiUnitsCount', 0),
    api_units_already_linked_count: readNested(
      value,
      'api_units_already_linked_count',
      'apiUnitsAlreadyLinkedCount',
      0
    ),
    suggested_matches_count: readNested(
      value,
      'suggested_matches_count',
      'suggestedMatchesCount',
      0
    ),
    suggested_exact_uprn_matches_count: readNested(
      value,
      'suggested_exact_uprn_matches_count',
      'suggestedExactUprnMatchesCount',
      0
    ),
    suggested_exact_name_matches_count: readNested(
      value,
      'suggested_exact_name_matches_count',
      'suggestedExactNameMatchesCount',
      0
    ),
    suggested_name_fragment_matches_count: readNested(
      value,
      'suggested_name_fragment_matches_count',
      'suggestedNameFragmentMatchesCount',
      0
    ),
    ambiguous_matches_count: readNested(
      value,
      'ambiguous_matches_count',
      'ambiguousMatchesCount',
      0
    ),
    unmatched_api_units_count: readNested(
      value,
      'unmatched_api_units_count',
      'unmatchedApiUnitsCount',
      0
    ),
    unmatched_existing_units_count: readNested(
      value,
      'unmatched_existing_units_count',
      'unmatchedExistingUnitsCount',
      0
    ),
    ignored_api_units_count: readNested(
      value,
      'ignored_api_units_count',
      'ignoredApiUnitsCount',
      0
    ),
    actionable_api_units_count: readNested(
      value,
      'actionable_api_units_count',
      'actionableApiUnitsCount',
      null
    ),
    reference_postcode_pages_checked: readNested(
      value,
      'reference_postcode_pages_checked',
      'referencePostcodePagesChecked',
      0
    ),
    reference_postcode_page_limit_reached: readNested(
      value,
      'reference_postcode_page_limit_reached',
      'referencePostcodePageLimitReached',
      false
    ),
    preview_result_summary: readNested(
      value,
      'preview_result_summary',
      'previewResultSummary'
    )
  })

  const normalizeApiUnit = value => ({
    ...value,
    api_unit_key: readNested(value, 'api_unit_key', 'apiUnitKey'),
    unit_name: readNested(value, 'unit_name', 'unitName'),
    building_name: readNested(value, 'building_name', 'buildingName'),
    building_type: readNested(value, 'building_type', 'buildingType'),
    building_type_label: readNested(
      value,
      'building_type_label',
      'buildingTypeLabel'
    ),
    classification_code: readNested(
      value,
      'classification_code',
      'classificationCode'
    ),
    suggested_existing_unit_id: readNested(
      value,
      'suggested_existing_unit_id',
      'suggestedExistingUnitId'
    ),
    suggested_match_type: readNested(
      value,
      'suggested_match_type',
      'suggestedMatchType'
    ),
    suggested_match_confidence: readNested(
      value,
      'suggested_match_confidence',
      'suggestedMatchConfidence'
    ),
    suggested_match_reason: readNested(
      value,
      'suggested_match_reason',
      'suggestedMatchReason'
    ),
    candidate_existing_unit_ids: readNested(
      value,
      'candidate_existing_unit_ids',
      'candidateExistingUnitIds',
      []
    ),
    requires_review: readNested(
      value,
      'requires_review',
      'requiresReview',
      false
    ),
    is_ignored: readNested(value, 'is_ignored', 'isIgnored', false),
    already_exists: readNested(value, 'already_exists', 'alreadyExists', false)
  })

  const normalizeExistingUnit = value => ({
    ...value,
    building_type: readNested(value, 'building_type', 'buildingType'),
    plot_number: readNested(value, 'plot_number', 'plotNumber'),
    floor_numbers: readNested(value, 'floor_numbers', 'floorNumbers'),
    files_count: readNested(value, 'files_count', 'filesCount', 0),
    private_files_count: readNested(
      value,
      'private_files_count',
      'privateFilesCount',
      0
    ),
    shared_files_count: readNested(
      value,
      'shared_files_count',
      'sharedFilesCount',
      0
    ),
    assigned_users_count: readNested(
      value,
      'assigned_users_count',
      'assignedUsersCount',
      0
    ),
    access_requests_count: readNested(
      value,
      'access_requests_count',
      'accessRequestsCount',
      0
    ),
    linked_records_count: readNested(
      value,
      'linked_records_count',
      'linkedRecordsCount',
      0
    ),
    has_linked_records: readNested(
      value,
      'has_linked_records',
      'hasLinkedRecords',
      false
    ),
    suggested_api_unit_key: readNested(
      value,
      'suggested_api_unit_key',
      'suggestedApiUnitKey'
    ),
    suggested_match_type: readNested(
      value,
      'suggested_match_type',
      'suggestedMatchType'
    ),
    suggested_match_confidence: readNested(
      value,
      'suggested_match_confidence',
      'suggestedMatchConfidence'
    ),
    suggested_match_reason: readNested(
      value,
      'suggested_match_reason',
      'suggestedMatchReason'
    )
  })

  const normalizeSuggestedMatch = value => ({
    ...value,
    api_unit_key: readNested(value, 'api_unit_key', 'apiUnitKey'),
    existing_unit_id: readNested(value, 'existing_unit_id', 'existingUnitId'),
    match_type: readNested(value, 'match_type', 'matchType'),
    candidate_existing_unit_ids: readNested(
      value,
      'candidate_existing_unit_ids',
      'candidateExistingUnitIds',
      []
    ),
    requires_review: readNested(
      value,
      'requires_review',
      'requiresReview',
      false
    )
  })

  return {
    ...data,
    summary: normalizeSummary(summary),
    api_units: Array.isArray(apiUnits) ? apiUnits.map(normalizeApiUnit) : [],
    existing_units: Array.isArray(existingUnits)
      ? existingUnits.map(normalizeExistingUnit)
      : [],
    suggested_matches: Array.isArray(suggestedMatches)
      ? suggestedMatches.map(normalizeSuggestedMatch)
      : [],
    preview: preview ? normalizeUnitSyncPreview(preview) : null,
  }
}

export const resolveNextNearbyRadiusIndex = data => {
  if (!data) {
    return null
  }

  const availableRadii = Array.isArray(data.available_nearby_radii)
    ? data.available_nearby_radii
    : []
  const nextRadius = data.nearby_next_radius

  if (!availableRadii.length || nextRadius == null) {
    return null
  }

  const nextIndex = availableRadii.findIndex(
    radius => Number(radius) === Number(nextRadius)
  )

  return nextIndex >= 0 ? nextIndex : null
}

// Floors pre-filled by the API after a unit sync (from public data unit
// names and EPC floor levels). Tell the user what was filled and how much of
// it is only a suggestion from the unit numbering.
export const notifyFloorPrefill = meta => {
  const prefill = meta?.floorPrefill || meta?.floor_prefill
  if (!prefill) return

  const prefilled = Number(prefill.prefilledCount ?? prefill.prefilled_count ?? 0)
  const review = Number(prefill.reviewCount ?? prefill.review_count ?? 0)
  const outside = Number(
    prefill.outsideBuildingCount ?? prefill.outside_building_count ?? 0
  )
  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`

  const building = prefill.buildingChanges || prefill.building_changes || {}
  const floorsNumber = Number(building.floorsNumber ?? building.floors_number ?? 0)

  const parts = []
  if (floorsNumber > 0) {
    parts.push(
      `Building floors set to ${floorsNumber} from the unit names.`
    )
  }
  if (prefilled > 0) {
    parts.push(`Floors pre-filled for ${plural(prefilled, 'unit', 'units')}.`)
    if (review > 0) {
      parts.push(
        `${plural(review, 'unit was', 'units were')} read from the unit number — please check ${review === 1 ? 'it' : 'them'}.`
      )
    }
  }
  if (outside > 0) {
    parts.push(
      `${plural(outside, 'unit names', 'units name')} a floor this building does not have yet — set the building floors, then sync again or fill them in by hand.`
    )
  }
  if (!parts.length) return

  toast.info(parts.join(' '))
}
